import { captureMessageForSentry } from '@/libraries/sentry';
import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import { createVfm } from 'vue-final-modal';

if (process.env.NODE_ENV !== 'production') {
	const originalWarn = console.warn;
	console.warn = (...args) => {
		if (args[0]?.includes?.('Write operation failed')) {
			console.trace('Vue readonly computed write:');
		}
		if (typeof args[0] === 'string' && args[0].includes('[Vue Router warn]')) {
			return;
		}
		originalWarn(...args);
	};
}

interface HydrationMismatchEntry {
	cause: string;
	node: Node | null;
}

declare global {
	interface Window {
		__JW_HYDRATION_MISMATCHES__?: HydrationMismatchEntry[];
		__JW_ON_HYDRATION_MISMATCH__?: (entry: HydrationMismatchEntry) => void;
	}
}

function isBrowserTranslationMutation({ node }: HydrationMismatchEntry): boolean {
	const el = node?.nodeType === Node.ELEMENT_NODE ? (node as Element) : node?.parentElement;
	if (!el) return false;

	// Google Translate wraps translated text in <font dir="auto"> elements
	if (el.matches('font[dir="auto"]') || el.querySelector('font[dir="auto"]') != null) return true;

	// Microsoft Edge's translator tags translated elements with _msttexthash/_msthash attributes
	const msTranslatorSelector = '[_msttexthash], [_msthash]';
	return el.closest(msTranslatorSelector) != null || el.querySelector(msTranslatorSelector) != null;
}

function describeDomNode(node: Node | null): string {
	if (!node) return '[unknown node]';
	const startEl = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
	if (!startEl) return `[detached ${node.nodeName}]`;
	const path: string[] = [];
	let el: Element | null = startEl;
	while (el && el.tagName !== 'HTML' && path.length < 10) {
		let part = el.tagName.toLowerCase();
		if (el.id) part += `#${el.id}`;
		else if (el.classList.length) part += `.${Array.from(el.classList).slice(0, 3).join('.')}`;
		if (el.parentElement) part += `:nth-child(${Array.prototype.indexOf.call(el.parentElement.children, el) + 1})`;
		path.unshift(part);
		el = el.parentElement;
	}
	return path.join(' > ');
}

if (import.meta.client) {
	const HYDRATION_REPORT_LIMIT = 10;
	let hydrationReportCount = 0;

	function reportHydrationMismatch(entry: HydrationMismatchEntry) {
		if (hydrationReportCount >= HYDRATION_REPORT_LIMIT) return;
		if (isBrowserTranslationMutation(entry)) return;
		hydrationReportCount++;
		const el = entry.node?.nodeType === Node.ELEMENT_NODE ? (entry.node as Element) : entry.node?.parentElement;
		captureMessageForSentry(
			'[HydrationBail]',
			{
				where: '[01.vue-setup.ts]: hydration mismatch hook',
				meta: {
					cause: entry.cause,
					selector: describeDomNode(entry.node),
					html: el?.outerHTML?.slice(0, 300) ?? '',
				},
			},
			'error',
			{ hydrationMismatchCause: entry.cause }
		);
	}

	(window.__JW_HYDRATION_MISMATCHES__ ?? []).forEach(reportHydrationMismatch);
	window.__JW_ON_HYDRATION_MISMATCH__ = reportHydrationMismatch;
}

export default defineNuxtPlugin({
	name: 'vue-setup',
	parallel: true,
	setup(nuxtApp) {
		nuxtApp.vueApp.config.performance = process.env.NODE_ENV === 'production';
		nuxtApp.vueApp.config.errorHandler = (error, _, info) => {
			// mObs - next steps: sentry may not be initialized yet here, consider a queue system to avoid lost errors
			captureMessageForSentry(
				'[Uncaught Error]:',
				{ where: '[main.ssr.ts]: Vue Error Handler', error: error as Error, meta: { info } },
				'error'
			);
		};

		globalThis.IS_TOUCH = import.meta.client ? !window.matchMedia('(hover: hover)').matches : false;
		nuxtApp.vueApp.use(GlobalComponentsPlugin);
		nuxtApp.vueApp.use(GlobalDirectivesPlugin);
		nuxtApp.vueApp.use(createVfm());

		if (process.env.NODE_ENV !== 'production') {
			import('@/plugins/cls-check').then(module => nuxtApp.vueApp.use(module.ClsCheckPlugin));
		}
	},
});
